import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import useSWR from "swr";
import api, { formatApiErrorDetail } from "@/lib/api";
import { formatIDR, formatDate, terbilang } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Printer, ChevronLeft, Building2, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const fetcher = (url) => api.get(url).then((r) => r.data);

export default function Receipt() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, error } = useSWR(`/payments/${id}/receipt`, fetcher);
  const [sendingWa, setSendingWa] = useState(false);

  const sendWhatsApp = async () => {
    setSendingWa(true);
    try {
      const { data: res } = await api.post(`/reminders/receipt/${id}`);
      toast.success(`Kwitansi terkirim via WhatsApp ke ${res.target}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Gagal kirim WhatsApp");
    } finally {
      setSendingWa(false);
    }
  };

  if (error) return <div className="text-sm text-destructive">Gagal memuat kwitansi.</div>;
  if (!data) return <div className="text-sm text-muted-foreground">Memuat...</div>;

  const { payment, bill, contract, unit, tenant, issued_by } = data;

  return (
    <div className="mx-auto max-w-3xl" data-testid="receipt-page">
      <div className="no-print mb-4 flex items-center justify-between">
        <Button variant="ghost" size="sm" className="gap-1 -ml-2" onClick={() => navigate(-1)}>
          <ChevronLeft className="h-4 w-4" /> Kembali
        </Button>
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="gap-2"
            disabled={sendingWa}
            onClick={sendWhatsApp}
            data-testid="send-whatsapp-receipt"
          >
            <MessageCircle className="h-4 w-4" /> {sendingWa ? "Mengirim..." : "Kirim via WhatsApp"}
          </Button>
          <Button className="gap-2" onClick={() => window.print()} data-testid="print-receipt-button">
            <Printer className="h-4 w-4" /> Cetak / Simpan PDF
          </Button>
        </div>
      </div>

      <div className="print-container bg-card border border-border/60 shadow-sm rounded-lg p-8 sm:p-12">
        <header className="flex items-start justify-between gap-6 pb-6 border-b border-border/60">
          <div className="flex items-center gap-3">
            <div className="h-11 w-11 rounded-md bg-primary text-primary-foreground grid place-items-center">
              <Building2 className="h-5 w-5" />
            </div>
            <div>
              <div className="font-display text-lg font-semibold tracking-tight">Aplikasi Kontrakan Surbakti</div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Kwitansi Pembayaran</div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">No. Kwitansi</div>
            <div className="font-mono text-sm mt-1">{payment.receipt_number}</div>
            <div className="text-xs text-muted-foreground mt-1">{formatDate(payment.paid_at)}</div>
          </div>
        </header>

        <section className="grid grid-cols-2 gap-6 py-6 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Diterima dari</div>
            <div className="font-medium">{tenant?.name}</div>
            {tenant?.phone && <div className="text-xs text-muted-foreground">{tenant.phone}</div>}
            {tenant?.id_number && <div className="text-xs text-muted-foreground">KTP: {tenant.id_number}</div>}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-1">Unit Kontrakan</div>
            <div className="font-medium">{unit?.code} — {unit?.name}</div>
            {unit?.address && <div className="text-xs text-muted-foreground">{unit.address}</div>}
          </div>
        </section>

        <section className="py-4 border-y border-border/60">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground">Uang sejumlah</div>
          <div className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-1">
            {formatIDR(payment.amount)}
          </div>
          <div className="italic text-sm text-muted-foreground mt-2 first-letter:uppercase">
            {terbilang(payment.amount)}
          </div>
        </section>

        <section className="py-6 space-y-2 text-sm">
          <div className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground mb-2">Untuk pembayaran</div>
          <Row label="Periode Sewa" value={`${formatDate(bill?.period_start)} – ${formatDate(bill?.period_end)}`} />
          <Row label="Jatuh Tempo" value={formatDate(bill?.due_date)} />
          <Row label="Nilai Tagihan" value={formatIDR(bill?.amount)} />
          {bill?.late_fee > 0 && <Row label="Denda Keterlambatan" value={formatIDR(bill.late_fee)} />}
          <Row label="Metode Pembayaran" value={payment.method === "cash" ? "Cash" : "Transfer"} />
          {payment.note && <Row label="Catatan" value={payment.note} />}
        </section>

        <section className="grid grid-cols-2 gap-6 pt-8 text-sm">
          <div>
            <div className="text-muted-foreground text-xs">Penyewa,</div>
            <div className="h-20 border-b border-dashed border-border/70" />
            <div className="mt-1 font-medium">{tenant?.name}</div>
          </div>
          <div className="text-right">
            <div className="text-muted-foreground text-xs">Diterima oleh,</div>
            <div className="h-20 border-b border-dashed border-border/70" />
            <div className="mt-1 font-medium">{issued_by?.name}</div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">{issued_by?.role}</div>
          </div>
        </section>

        <footer className="mt-8 pt-4 border-t border-border/60 flex items-center justify-between text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          <span>Dokumen ini sah tanpa cap basah.</span>
          <span>Aplikasi Kontrakan Surbakti · {new Date().getFullYear()}</span>
        </footer>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3 py-1">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-medium">{value || "—"}</span>
    </div>
  );
}
